/**
 * Front-end design system — the shared visual language of every menu screen.
 * (§9.1, §9.4)
 *
 * ── Why this module exists ──────────────────────────────────────────────────
 *
 * menu.ts grew organically: it owned `el()`, `segmented()`, a `menuButton()`
 * closure and one ~200-line CSS string. Career / World Cup / Squads are three
 * MORE screens that need exactly the same builders and exactly the same chrome,
 * and a fourth copy of a panel gradient is how a front end stops looking like
 * one product. So the builders and the stylesheet moved here, menu.ts imports
 * them, and every screen — old and new — is styled by one file.
 *
 * ── The look, stated once ───────────────────────────────────────────────────
 *
 * Broadcast-dark navy panels, ONE gold accent (#ffd94a, already the game's
 * accent in hud.ts / pauseMenu.ts) plus a cool secondary for "live/active"
 * states. Heavy condensed uppercase type with wide tracking for titles, quiet
 * 62%-white body copy. Content sits in a wide 1180px stage with a persistent
 * top bar carrying the nav tabs, so moving between HOME / CAREER / WORLD CUP /
 * TEAMS never changes where the player's eye is.
 *
 * All motion is opacity/transform only, same rule as menu.ts's own entrance
 * animations: no layout thrash, nothing on a JS timer except screen swaps. The
 * `prefers-reduced-motion` block at the bottom of menu.ts's CSS already covers
 * this file's animations too (it matches `.fgmenu *`).
 *
 * ── Keyboard ────────────────────────────────────────────────────────────────
 *
 * Tab/Shift-Tab is native and untouched. `createArrowNav` adds the console-style
 * layer on top: arrows move focus between `[data-fgnav]` elements, Enter/Space
 * are the browser's own button activation. It deliberately does nothing while a
 * text field has focus, because arrow keys move a caret there.
 */

/** The one accent, as a JS constant for the rare inline style that needs it. */
export const ACCENT = '#ffd94a';

// ---------------------------------------------------------------------------
// Builders
// ---------------------------------------------------------------------------

/** `document.createElement` with class + text in one call. */
export function el(tag: string, className: string, text = ''): HTMLElement {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

/** A `<button>` that participates in arrow-key nav. */
export function menuButton(label: string, cls: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.className = `fgmenu-btn ${cls}`.trim();
  b.textContent = label;
  b.dataset['fgnav'] = '1';
  b.addEventListener('click', onClick);
  return b;
}

/** Game-style segmented picker. Returns the row; calls onPick on change. */
export function segmented<T extends string | number>(
  label: string,
  choices: ReadonlyArray<{ value: T; label: string }>,
  initial: T,
  onPick: (v: T) => void,
): HTMLElement {
  const row = el('div', 'fgmenu-segrow');
  row.appendChild(el('span', 'fgmenu-seglabel', label));
  const group = el('div', 'fgmenu-seg');
  const buttons = new Map<T, HTMLButtonElement>();
  for (const c of choices) {
    const b = document.createElement('button');
    b.className = 'fgmenu-segbtn';
    b.textContent = c.label;
    b.dataset['fgnav'] = '1';
    if (c.value === initial) b.classList.add('fgmenu-segon');
    b.addEventListener('click', () => {
      for (const other of buttons.values()) other.classList.remove('fgmenu-segon');
      b.classList.add('fgmenu-segon');
      onPick(c.value);
    });
    buttons.set(c.value, b);
    group.appendChild(b);
  }
  row.appendChild(group);
  return row;
}

export interface TileOptions {
  title: string;
  desc: string;
  /** Small pill in the corner: 'TRAINING', 'ONLINE', 'NEW'… */
  badge?: string;
  /** Kicker line above the title (competition name, "CONTINUE", …). */
  eyebrow?: string;
  /** Omit for a non-interactive tile (the join-with-code card is one). */
  onClick?: () => void;
  /** Marks the tile as the current section in the nav sense. */
  active?: boolean;
  /** Extra classes: `fgfe-tile-lg` spans two columns, `fgfe-tile-online` tints. */
  cls?: string;
}

/**
 * The core FIFA-style mode tile: accent edge, eyebrow, big title, quiet blurb,
 * lift on hover/focus. A tile with no `onClick` renders as a `<div>` so it is
 * neither focusable nor clickable — that is how the join-with-code card (which
 * carries its own input + button) stays a container rather than a trap.
 */
export function tile(opts: TileOptions): HTMLElement {
  const node = el(opts.onClick ? 'button' : 'div', `fgfe-tile ${opts.cls ?? ''}`.trim());
  if (opts.active) node.classList.add('fgfe-tile-on');
  if (opts.badge) node.appendChild(el('span', 'fgfe-badge', opts.badge));
  if (opts.eyebrow) node.appendChild(el('span', 'fgfe-eyebrow', opts.eyebrow));
  node.appendChild(el('span', 'fgfe-tiletitle', opts.title));
  node.appendChild(el('span', 'fgfe-tiledesc', opts.desc));
  if (opts.onClick) {
    node.dataset['fgnav'] = '1';
    node.addEventListener('click', opts.onClick);
  }
  return node;
}

export interface HeroOptions {
  eyebrow: string;
  title: string;
  /** Two or three short "SEASON 1 · MATCHDAY 4" style facts. */
  meta: readonly string[];
  actionLabel: string;
  onAction: () => void;
  /** Optional quiet second action ("SQUAD", "RESET SEASON"). */
  secondLabel?: string;
  onSecond?: () => void;
}

/**
 * The featured panel: the one thing the screen wants you to press. Uses the
 * same stadium art as the title background, dimmed hard so type stays legible
 * at any brightness (the art is a photo — a light patch behind white 30px type
 * is unreadable, hence the double gradient in the CSS).
 */
export function hero(opts: HeroOptions): HTMLElement {
  const node = el('div', 'fgfe-hero');
  const body = el('div', 'fgfe-herobody');
  body.appendChild(el('span', 'fgfe-eyebrow', opts.eyebrow));
  body.appendChild(el('h2', 'fgfe-herotitle', opts.title));
  if (opts.meta.length) {
    const meta = el('div', 'fgfe-herometa');
    for (const m of opts.meta) meta.appendChild(el('span', 'fgfe-metachip', m));
    body.appendChild(meta);
  }
  const actions = el('div', 'fgfe-heroactions');
  actions.appendChild(menuButton(opts.actionLabel, 'fgmenu-primary fgfe-cta', opts.onAction));
  if (opts.secondLabel && opts.onSecond) {
    actions.appendChild(menuButton(opts.secondLabel, 'fgfe-ghost2', opts.onSecond));
  }
  body.appendChild(actions);
  node.appendChild(body);
  return node;
}

/** Which nav tab is lit. menu.ts owns navigation; the screens just declare
 *  where they are so the top bar reads correctly on every one of them. */
export type NavKey = 'home' | 'play' | 'career' | 'worldcup' | 'teams';

export interface NavTab {
  label: string;
  onClick: () => void;
  active?: boolean;
}

/**
 * Persistent top bar: brand mark, nav tabs, right-hand identity chip. Lives
 * INSIDE each screen (as its first child) rather than in the flow root, so it
 * inherits the per-screen stagger animation and always aligns to the screen's
 * own content width instead of the viewport.
 */
export function topBar(tabs: readonly NavTab[], rightText: string): HTMLElement {
  const bar = el('header', 'fgfe-topbar');

  const logo = document.createElement('img');
  logo.className = 'fgmenu-logo fgfe-topmark';
  logo.src = '/branding/footy-arena-logo.png';
  logo.alt = 'Footy Arena';
  logo.draggable = false;
  bar.appendChild(logo);

  const nav = el('nav', 'fgfe-tabs');
  for (const t of tabs) {
    const b = document.createElement('button');
    b.className = `fgfe-tab${t.active ? ' fgfe-tabon' : ''}`;
    b.textContent = t.label;
    b.dataset['fgnav'] = '1';
    b.addEventListener('click', t.onClick);
    nav.appendChild(b);
  }
  bar.appendChild(nav);

  const chip = el('div', 'fgfe-idchip');
  chip.appendChild(el('span', 'fgfe-idlabel', 'PLAYING AS'));
  chip.appendChild(el('span', 'fgfe-idname', rightText || 'Guest'));
  bar.appendChild(chip);
  return bar;
}

/** Section header with an optional right-aligned hint. */
export function sectionHead(title: string, hint = ''): HTMLElement {
  const head = el('div', 'fgfe-sechead');
  head.appendChild(el('h3', 'fgfe-sectitle', title));
  if (hint) head.appendChild(el('span', 'fgfe-sechint', hint));
  return head;
}

/** A bordered content card — the container every list/table sits in. */
export function panel(cls = ''): HTMLElement {
  return el('div', `fgfe-panel ${cls}`.trim());
}

/**
 * Team crest stand-in: initials in the team's kit colours. Deliberately NOT a
 * badge image — the game ships no club badges by design (§16 licensing), so the
 * "crest" is generated from the invented team's own two colours.
 */
export function crest(short: string, primary: string, secondary: string, cls = ''): HTMLElement {
  const node = el('span', `fgfe-crest ${cls}`.trim(), short);
  node.style.setProperty('--c1', primary);
  node.style.setProperty('--c2', secondary);
  return node;
}

/**
 * The mark for a team: a real national flag for nations, generated initials for
 * clubs.
 *
 * The split is a licensing one, not a stylistic one. Clubs here are invented, so
 * there IS no real crest and initials are the honest representation. Nations are
 * real, and their flags are public national symbols shipped as verified MIT
 * SVGs (docs/THIRD_PARTY_NOTICES.md) — never drawn or approximated, because an
 * inaccurate national flag is worse than none.
 *
 * A flag that fails to load falls back to the initials crest rather than leaving
 * a broken-image box, so the row still identifies the team.
 */
export function teamMark(
  team: { short: string; primary: string; secondary: string; flag?: string },
  cls = '',
): HTMLElement {
  if (team.flag === undefined) return crest(team.short, team.primary, team.secondary, cls);

  const wrap = el('span', `fgfe-crest fgfe-flag ${cls}`.trim());
  wrap.style.setProperty('--c1', team.primary);
  wrap.style.setProperty('--c2', team.secondary);
  const img = document.createElement('img');
  img.src = `/ui/flags/${team.flag}.svg`;
  img.alt = '';
  img.setAttribute('aria-hidden', 'true');
  img.draggable = false;
  img.onerror = (): void => {
    wrap.classList.remove('fgfe-flag');
    wrap.textContent = team.short;
  };
  wrap.appendChild(img);
  return wrap;
}

export interface ArrowNav {
  dispose(): void;
}

/**
 * Console-style arrow navigation over the CURRENT screen.
 *
 * `getScreen` is re-read on every key because the flow swaps screen elements;
 * caching the element list would keep focusing a detached node one screen
 * later. Text inputs are exempt (arrows move a caret) and so are chorded
 * presses (ctrl/meta/alt belong to the browser).
 */
export function createArrowNav(getScreen: () => HTMLElement | null): ArrowNav {
  const onKey = (e: KeyboardEvent): void => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const key = e.key;
    const forward = key === 'ArrowRight' || key === 'ArrowDown';
    const back = key === 'ArrowLeft' || key === 'ArrowUp';
    if (!forward && !back) return;
    const active = document.activeElement;
    if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) return;
    const screen = getScreen();
    if (!screen) return;
    const items = Array.from(
      screen.querySelectorAll<HTMLElement>('[data-fgnav]'),
    ).filter((n) => n.offsetParent !== null);
    if (!items.length) return;
    const at = active instanceof HTMLElement ? items.indexOf(active) : -1;
    const next = at < 0 ? 0 : (at + (forward ? 1 : -1) + items.length) % items.length;
    items[next]?.focus();
    e.preventDefault();
  };
  window.addEventListener('keydown', onKey);
  return {
    dispose(): void {
      window.removeEventListener('keydown', onKey);
    },
  };
}

// ---------------------------------------------------------------------------
// Stylesheet
// ---------------------------------------------------------------------------

/**
 * Appended after menu.ts's own CSS so the `fgfe-*` rules and the handful of
 * deliberate `fgmenu-*` refinements below win the cascade. Everything is scoped
 * under `.fgmenu` (the flow root) so nothing here can leak into the HUD.
 */
/**
 * Styling for the hub screens (Career, World Cup, Settings, Squad).
 *
 * Injected as a <style> tag by menu.ts rather than imported, because these
 * screens predate the Vite CSS pipeline footy-arena.css goes through.
 *
 * It defines NO colours, radii, spacing or timings of its own — every value is
 * a var() from footy-arena.css. There is one design system; this is its second
 * consumer, not a second system.
 */
export const FIFA_CSS = `
/* ===========================================================================
   HUB SCREENS — Career, World Cup, Settings, Squad
   ---------------------------------------------------------------------------
   These four screens used to carry their own look: yellow #ffd94a, bevelled
   buttons, 3px-radius boxes and flat panels. Going one level down from the
   dashboard changed the game's visual language completely.

   They now read from the SAME tokens as the menu (footy-arena.css) — navy
   surfaces, steel-blue copy, one gold accent, one hairline. Nothing here
   defines a colour of its own; every value is a var(). That is the whole point:
   there is one design system, and this file is a second consumer of it, not a
   second system.

   Layout language, shared with the menu:
     - a screen is a safe-area column, never a full-bleed sheet
     - a panel is a translucent navy plate with one hairline
     - a section head is a gold tick + uppercase title
     - a row is a grid, never a float, so columns line up down the whole list
   =========================================================================== */

.fgfe-screen {
  position: relative;
  z-index: 3;
  box-sizing: border-box;
  height: 100%;
  width: min(calc(100% - var(--fa-gutter) * 2), var(--fa-max-w));
  margin-inline: auto;
  padding-block: var(--fa-top-pad) var(--fa-bottom-pad);
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  font-family: var(--fa-ui);
  color: var(--fa-text);
  overflow: hidden;
}

/* ── Top bar ──────────────────────────────────────────────────────────────
   Same bordered, blurred cluster as the dashboard's utility bar, so moving
   between levels does not change what chrome looks like. */
.fgfe-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: none;
  height: 52px;
  padding-inline: var(--space-2);
  border: 1px solid var(--fa-hairline);
  border-radius: var(--fa-r-lg);
  background: rgba(10, 16, 24, 0.62);
  backdrop-filter: blur(10px);
}
.fgfe-tab {
  height: 38px;
  padding-inline: var(--space-5);
  border: 0;
  border-radius: var(--fa-r-md);
  background: transparent;
  color: var(--fa-steel);
  font-family: var(--fa-ui);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.1em;
  cursor: pointer;
  transition: background var(--fa-t-fast) var(--fa-ease), color var(--fa-t-fast) var(--fa-ease);
}
.fgfe-tab:hover, .fgfe-tab:focus-visible {
  background: rgba(255, 255, 255, 0.06);
  color: var(--fa-text);
  outline: none;
}
.fgfe-tab-on { background: var(--fa-accent-soft); color: var(--fa-accent); }
.fgfe-idchip { display: flex; align-items: center; gap: var(--space-3); padding-right: var(--space-4); }
.fgfe-idlabel { font-size: 10px; font-weight: 700; letter-spacing: 0.16em; text-transform: uppercase; color: var(--fa-steel-dim); }
.fgfe-idname { font-size: 14px; font-weight: 700; color: var(--fa-text); }

/* ── Hero strip ───────────────────────────────────────────────────────────
   A short identity band, not a second cinematic hero — that job belongs to the
   dashboard. Gold tick, eyebrow, title, meta chips. */
.fgfe-hero {
  position: relative;
  flex: none;
  display: flex;
  align-items: center;
  gap: var(--space-7);
  padding: var(--space-6) var(--space-7);
  border: 1px solid var(--fa-hairline);
  border-radius: var(--fa-r-lg);
  background: linear-gradient(100deg, rgba(18, 29, 44, 0.9) 0%, rgba(7, 12, 20, 0.72) 62%, rgba(7, 12, 20, 0.4) 100%);
  overflow: hidden;
}
.fgfe-hero::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 96px;
  height: 3px;
  background: var(--fa-accent);
}
.fgfe-herobody { flex: 1 1 auto; min-width: 0; }
.fgfe-eyebrow {
  margin: 0 0 var(--space-2);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--fa-accent);
}
.fgfe-herotitle {
  margin: 0;
  font-family: var(--fa-display);
  font-size: clamp(28px, 2.4vw, 40px);
  font-weight: 700;
  line-height: 1.02;
  letter-spacing: 0.005em;
}
.fgfe-herometa { display: flex; flex-wrap: wrap; gap: var(--space-2); margin-top: var(--space-3); }
.fgfe-metachip {
  padding: 5px var(--space-3);
  border: 1px solid var(--fa-hairline);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.03);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fa-steel);
}
.fgfe-heroactions { display: flex; gap: var(--space-3); flex: none; }

/* ── Columns ──────────────────────────────────────────────────────────────
   min-height:0 on the tracks is what lets an inner list scroll instead of
   pushing the screen taller than the viewport. */
.fgfe-hub { display: flex; flex-direction: column; gap: var(--space-5); flex: 1 1 auto; min-height: 0; }
.fgfe-cols2, .fgfe-cols3 { display: grid; gap: var(--space-5); flex: 1 1 auto; min-height: 0; }
/* Opt-in: panels size to their CONTENT instead of stretching down the column.
   Only for screens whose panels are short forms — a setup screen's two panels
   stretched to full height were ~45% empty each, which reads as a rendering
   fault rather than as space. NOT the default: the career table and the squad
   editor rely on a stretched panel to give an inner list something to scroll
   inside of. */
/* "align-items: start" stops the panels stretching; "align-content: center"
   then centres the row they sit in, so the pair is a block in the middle of the
   screen rather than two short panels pinned to the top with a void beneath. */
.fgfe-cols-top { align-items: start; align-content: center; }
.fgfe-cols2 { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
.fgfe-cols3 { grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr) minmax(0, 1fr); }
.fgfe-l, .fgfe-d { min-width: 0; min-height: 0; }

/* ── Panel ───────────────────────────────────────────────────────────────── */
.fgfe-panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: var(--space-6);
  border: 1px solid var(--fa-hairline);
  border-radius: var(--fa-r-lg);
  background: linear-gradient(165deg, rgba(18, 29, 44, 0.82), rgba(7, 12, 20, 0.88));
}
.fgfe-panel .fgfe-topbar { background: none; border: 0; backdrop-filter: none; }

.fgfe-sechead {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-4);
  flex: none;
  margin-bottom: var(--space-5);
}
.fgfe-sectitle {
  position: relative;
  margin: 0;
  padding-left: var(--space-4);
  font-family: var(--fa-display);
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--fa-text);
}
/* Gold tick, same gesture as the dashboard eyebrow — rotated to a vertical
   rule because these titles sit inline rather than above a display line. */
.fgfe-sectitle::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 15px;
  border-radius: 2px;
  background: var(--fa-accent);
}
.fgfe-sechint { font-size: 12px; font-weight: 600; letter-spacing: 0.06em; color: var(--fa-steel-dim); }

/* ── Scroll region ───────────────────────────────────────────────────────── */
.fgfe-scroll { flex: 1 1 auto; min-height: 0; overflow-y: auto; scrollbar-gutter: stable; padding-right: 2px; }
.fgfe-scroll::-webkit-scrollbar { width: 10px; }
.fgfe-scroll::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.12);
  border-radius: 99px;
  border: 3px solid transparent;
  background-clip: content-box;
}

/* ── Rows ─────────────────────────────────────────────────────────────────
   One grid template for every list row in the game, so crest, name, detail and
   trailing value line up down the whole column instead of drifting per row. */
.fgfe-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
  padding: var(--space-3) var(--space-4);
  margin-bottom: var(--space-2);
  border: 1px solid var(--fa-hairline);
  border-radius: var(--fa-r-md);
  background: rgba(255, 255, 255, 0.022);
  color: var(--fa-text);
  font-family: var(--fa-ui);
  text-align: left;
  cursor: pointer;
  transition: border-color var(--fa-t-fast) var(--fa-ease),
              background var(--fa-t-fast) var(--fa-ease),
              transform var(--fa-t-fast) var(--fa-ease);
}
.fgfe-row:hover, .fgfe-row:focus-visible {
  border-color: var(--fa-accent-line);
  background: rgba(255, 255, 255, 0.05);
  transform: translateX(2px);
  outline: none;
}
.fgfe-row-on { border-color: var(--fa-accent); background: var(--fa-accent-soft); }
.fgfe-row-static { cursor: default; }
.fgfe-row-static:hover { transform: none; border-color: var(--fa-hairline); background: rgba(255, 255, 255, 0.022); }
/* The matchup row is FIVE cells (crest, name, "vs", name, crest), not the three
   a list row is. Left on \`.fgfe-row\`'s "auto | 1fr | auto" template the last two
   children wrapped onto an implicit second row, so the away side printed under
   the home side with "vs" stranded at the far right. */
.fgfe-vsrow {
  grid-template-columns: auto minmax(0, 1fr) auto minmax(0, 1fr) auto;
  justify-items: center;
  gap: var(--space-4);
}
.fgfe-vsrow .fgfe-rowend {
  padding: 2px 10px;
  border: 1px solid var(--fa-hairline);
  border-radius: 999px;
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--fa-steel-dim);
}
.fgfe-rowmain { min-width: 0; }
.fgfe-rowtitle { font-size: 14.5px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fgfe-rowsub { font-size: 12px; color: var(--fa-steel-dim); margin-top: 1px; }
.fgfe-rowend { font-size: 13px; font-weight: 700; color: var(--fa-steel); white-space: nowrap; }

/* ── Table ────────────────────────────────────────────────────────────────
   League tables are read by column, so the numeric columns are tabular-figure
   aligned and the header is sticky — scrolling a 16-row table must not lose
   which column is which. */
.fgfe-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.fgfe-table th {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: var(--space-2) var(--space-3);
  background: var(--fa-ink-1);
  border-bottom: 1px solid var(--fa-hairline-hi);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--fa-steel-dim);
  text-align: right;
}
.fgfe-table th:first-child, .fgfe-table td:first-child { text-align: left; }
.fgfe-table th:nth-child(2), .fgfe-table td:nth-child(2) { text-align: left; }
.fgfe-table td {
  padding: var(--space-3);
  border-bottom: 1px solid var(--fa-hairline);
  color: var(--fa-steel);
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.fgfe-table tr:hover td { background: rgba(255, 255, 255, 0.03); color: var(--fa-text); }
.fgfe-table .fgfe-row-on td { background: var(--fa-accent-soft); color: var(--fa-text); }
.fgfe-tname { display: inline-flex; align-items: center; gap: var(--space-3); min-width: 0; }

/* ── Form guide pills ─────────────────────────────────────────────────────
   Result colours are the one place a hue other than gold is allowed: a form
   guide that is all one colour is not a form guide. */
.fgfe-form { display: inline-flex; gap: 3px; }
.fgfe-pill {
  width: 18px; height: 18px;
  display: inline-grid; place-items: center;
  border-radius: 3px;
  font-size: 10px; font-weight: 800;
  color: var(--fa-ink-0);
  background: var(--fa-steel-dim);
}
.fgfe-pill[data-r="W"] { background: #6fcf78; }
.fgfe-pill[data-r="D"] { background: #8fa6c0; }
.fgfe-pill[data-r="L"] { background: #e0655f; }

/* ── Crest / flag ─────────────────────────────────────────────────────────
   Kept from the previous system because it is a data-driven mark, not styling:
   invented clubs get initials in their own kit colours, real nations get a
   real flag. See teamMark(). */
.fgfe-crest {
  display: inline-grid; place-items: center; flex: 0 0 auto;
  width: 24px; height: 24px; border-radius: 4px;
  font-size: 8.5px; font-weight: 900; letter-spacing: 0.4px;
  color: var(--c2); background: var(--c1);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.22);
}
.fgfe-crest-lg { width: 48px; height: 48px; border-radius: 6px; font-size: 15px; }
.fgfe-flag { background: var(--fa-ink-0); overflow: hidden; }
.fgfe-flag img { width: 100%; height: 100%; object-fit: cover; display: block; }

/* ── Buttons ─────────────────────────────────────────────────────────────
   The old .fgmenu-btn bevel is replaced wholesale. Three weights only:
   primary (gold), ghost (glass), danger (red hairline). */
.fgmenu-btn {
  height: 44px;
  padding-inline: var(--space-5);
  border: 1px solid var(--fa-hairline-hi);
  border-radius: var(--fa-r-md);
  background: rgba(255, 255, 255, 0.04);
  color: var(--fa-text);
  font-family: var(--fa-ui);
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.02em;
  cursor: pointer;
  box-shadow: none;
  transition: background var(--fa-t-fast) var(--fa-ease),
              border-color var(--fa-t-fast) var(--fa-ease),
              transform var(--fa-t-fast) var(--fa-ease);
}
.fgmenu-btn:hover, .fgmenu-btn:focus-visible {
  background: rgba(255, 255, 255, 0.08);
  border-color: var(--fa-hairline-hi);
  color: var(--fa-text);
  transform: none;
  outline: none;
}
.fgmenu-btn:focus-visible { border-color: var(--fa-accent); box-shadow: 0 0 0 2px var(--fa-accent-soft); }
.fgmenu-primary {
  border: 0;
  background: linear-gradient(180deg, var(--fa-accent-hi), var(--fa-accent-lo));
  color: var(--fa-on-accent);
  font-weight: 800;
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.4);
}
.fgmenu-primary:hover, .fgmenu-primary:focus-visible { filter: brightness(1.06); color: var(--fa-on-accent); }
.fgmenu-ghost { background: transparent; border-style: solid; color: var(--fa-steel); }
.fgfe-cta {
  height: 52px;
  padding-inline: var(--space-8);
  font-size: 15px;
  /* NOT width:100%. Stretched across the hero it read as a full-width banner
     rather than as a button, and on the World Cup screen the disabled "pick a
     nation below" hint became the loudest object on the page. */
  min-width: 200px;
}
/* Buttons never wrap their label onto a second line. */
.fgmenu-btn, .fgfe-tab, .fgmenu-segbtn { white-space: nowrap; }
.fgfe-ghost2 { background: transparent; color: var(--fa-steel); }
.fgfe-danger { border-color: rgba(224, 101, 95, 0.5); color: #e0655f; background: rgba(224, 101, 95, 0.08); }
.fgfe-danger:hover { background: rgba(224, 101, 95, 0.16); color: #ff8f8f; }
.fgfe-actions { display: flex; gap: var(--space-3); flex-wrap: wrap; flex: none; }
.fgfe-actions .fgmenu-btn { flex: 0 0 auto; }
.fgfe-note { margin: var(--space-4) 0 0; font-size: 12.5px; line-height: 1.55; color: var(--fa-steel-dim); }

/* ── Segmented control (settings) ────────────────────────────────────────
   Reuses the destination-screen segmented look so Settings and Create Room are
   visibly the same control. */
.fgmenu-segrow {
  display: grid;
  grid-template-columns: minmax(0, 200px) 1fr;
  align-items: center;
  gap: var(--space-5);
  padding: var(--space-4) 0;
  border-bottom: 1px solid var(--fa-hairline);
}
.fgmenu-seglabel {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: var(--fa-steel);
  text-transform: none;
  flex: none;
}
.fgmenu-seg {
  display: inline-flex;
  gap: 0;
  padding: 4px;
  border: 1px solid var(--fa-hairline);
  border-radius: var(--fa-r-md);
  background: rgba(6, 10, 17, 0.6);
  flex-wrap: wrap;
}
.fgmenu-segbtn {
  min-width: 62px;
  height: 34px;
  padding-inline: var(--space-4);
  border: 0;
  border-radius: var(--fa-r-sm);
  background: transparent;
  color: var(--fa-steel);
  font-family: var(--fa-ui);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: background var(--fa-t-fast) var(--fa-ease), color var(--fa-t-fast) var(--fa-ease);
}
.fgmenu-segbtn:hover { background: rgba(255, 255, 255, 0.05); color: var(--fa-text); }
.fgmenu-segbtn:focus-visible { outline: none; box-shadow: inset 0 0 0 2px var(--fa-accent-soft); color: var(--fa-text); }
.fgmenu-segon {
  background: linear-gradient(180deg, var(--fa-accent-hi), var(--fa-accent-lo));
  color: var(--fa-on-accent);
  font-weight: 700;
  border-color: transparent;
}

/* ── Pickers (club / nation select) ─────────────────────────────────────── */
/* Card floor is per-list, NOT global. Widening it globally to fit club names
   pushed the 32-nation World Cup list from six columns to four — eight rows,
   which overflowed the panel and left the whole tail of the list UNREACHABLE,
   because nothing here scrolled. Nation names ("Egypt", "Ghana", "Iran") never
   needed the width; club names do. */
.fgfe-pickgrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(var(--fgfe-pick-min, 196px), 1fr));
  gap: var(--space-3);
  /* Safety net, and the actual lesson from the above: a list that outgrows its
     panel must scroll rather than silently extend past the viewport. Needs
     min-height:0 to be allowed to shrink inside the flex-column panel. */
  min-height: 0;
  overflow-y: auto;
  scrollbar-gutter: stable;
}
.fgfe-pickgrid::-webkit-scrollbar { width: 10px; }
.fgfe-pickgrid::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.12);
  border-radius: 999px;
}

/* The club list opts in. Its 196px cards left the name column 97px, and
   THIRTEEN OF EIGHTEEN club names need more than that — "Ashcombe Wanderers"
   wants 150px and rendered as "Ashcombe ...", so the club-select screen could
   not tell you which club you were selecting. Non-name overhead (crest, gaps,
   rating, padding) measures 139px, so a card needs 289px for the longest name;
   300px is that with a margin. */
.fgfe-pickgrid-wide { --fgfe-pick-min: 300px; }
.fgfe-pick {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-4);
  border: 1px solid var(--fa-hairline);
  border-radius: var(--fa-r-md);
  background: linear-gradient(165deg, rgba(18, 29, 44, 0.7), rgba(7, 12, 20, 0.8));
  color: var(--fa-text);
  font-family: var(--fa-ui);
  text-align: left;
  cursor: pointer;
  transition: border-color var(--fa-t-fast) var(--fa-ease), transform var(--fa-t-fast) var(--fa-ease);
}
.fgfe-pick:hover, .fgfe-pick:focus-visible {
  border-color: var(--fa-accent-line);
  transform: translateY(-2px);
  outline: none;
}
.fgfe-pick-on { border-color: var(--fa-accent); background: var(--fa-accent-soft); }
.fgfe-rating {
  margin-left: auto;
  font-family: var(--fa-display);
  font-size: 19px;
  font-weight: 700;
  color: var(--fa-accent);
  font-variant-numeric: tabular-nums;
}

/* ── Bracket ─────────────────────────────────────────────────────────────── */
.fgfe-bracket { display: flex; gap: var(--space-4); align-items: stretch; }
.fgfe-round { flex: 1 1 0; min-width: 0; display: flex; flex-direction: column; gap: var(--space-2); }
.fgfe-roundname {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--fa-steel-dim);
  margin-bottom: var(--space-2);
}

/* ── Badges ──────────────────────────────────────────────────────────────── */
.fgfe-badge {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding-inline: var(--space-3);
  border: 1px solid var(--fa-hairline);
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--fa-steel-dim);
}
.fgfe-badge-live { border-color: var(--fa-accent-line); background: var(--fa-accent-soft); color: var(--fa-accent); }

/* ── Squad pitch ─────────────────────────────────────────────────────────
   A real pitch, drawn in CSS: centre line, centre circle and penalty boxes.
   The formation is read positionally, so the surface has to look like a pitch
   for the slot positions to mean anything. */
.fgfe-pitch {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  border: 1px solid var(--fa-hairline);
  border-radius: var(--fa-r-lg);
  background:
    repeating-linear-gradient(180deg, rgba(255,255,255,0.022) 0 8.33%, rgba(0,0,0,0) 8.33% 16.66%),
    linear-gradient(180deg, #12321f 0%, #0d2617 55%, #0a1c12 100%);
  overflow: hidden;
}
/* Centre line + centre circle. */
.fgfe-pitch::before {
  content: '';
  position: absolute;
  left: 6%; right: 6%; top: 50%;
  height: 1px;
  background: rgba(255, 255, 255, 0.16);
}
.fgfe-pitch::after {
  content: '';
  position: absolute;
  left: 50%; top: 50%;
  width: 116px; height: 116px;
  margin: -58px 0 0 -58px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 50%;
}
/* Penalty boxes, as their own element so they do not fight the pseudo-elements. */
.fgfe-pitch-boxes { position: absolute; inset: 0; pointer-events: none; }
.fgfe-pitch-boxes::before,
.fgfe-pitch-boxes::after {
  content: '';
  position: absolute;
  left: 26%; right: 26%;
  height: 13%;
  border: 1px solid rgba(255, 255, 255, 0.14);
}
.fgfe-pitch-boxes::before { top: 0; border-top: 0; }
.fgfe-pitch-boxes::after { bottom: 0; border-bottom: 0; }

.fgfe-slot {
  position: absolute;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 0;
  border: 0;
  background: none;
  color: var(--fa-text);
  font-family: var(--fa-ui);
  cursor: pointer;
}
.fgfe-slotchip {
  width: 40px; height: 40px;
  display: grid; place-items: center;
  border-radius: 50%;
  border: 1.5px solid rgba(255, 255, 255, 0.34);
  background: linear-gradient(180deg, rgba(20, 32, 48, 0.96), rgba(9, 15, 24, 0.96));
  font-family: var(--fa-display);
  font-size: 15px;
  font-weight: 700;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.5);
  transition: border-color var(--fa-t-fast) var(--fa-ease),
              box-shadow var(--fa-t-fast) var(--fa-ease),
              transform var(--fa-t-fast) var(--fa-ease);
}
.fgfe-slot:hover .fgfe-slotchip,
.fgfe-slot:focus-visible .fgfe-slotchip {
  border-color: var(--fa-accent);
  transform: scale(1.08);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.6), 0 0 22px -4px var(--fa-accent-line);
}
.fgfe-slot:focus-visible { outline: none; }
.fgfe-slot-on .fgfe-slotchip {
  border-color: var(--fa-accent);
  background: linear-gradient(180deg, var(--fa-accent-hi), var(--fa-accent-lo));
  color: var(--fa-on-accent);
}
.fgfe-slotname {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.02em;
  max-width: 84px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.9);
}
.fgfe-slotpos { font-size: 9px; font-weight: 700; letter-spacing: 0.1em; color: var(--fa-accent); }

/* Position colours — data, not decoration: GK/DEF/MID/FWD must be separable at
   a glance in a 25-row squad list. */
.fgfe-pos {
  display: inline-grid; place-items: center;
  min-width: 34px; height: 20px;
  padding-inline: 6px;
  border-radius: 3px;
  font-size: 10px; font-weight: 800; letter-spacing: 0.06em;
  color: var(--fa-ink-0);
}
.fgfe-pos-gk  { background: #e8c56a; }
.fgfe-pos-def { background: #7fa7d4; }
.fgfe-pos-mid { background: #6fcf9f; }
.fgfe-pos-fwd { background: #e0857f; }

.fgfe-squadhead { display: flex; align-items: center; gap: var(--space-4); flex-wrap: wrap; flex: none; margin-bottom: var(--space-4); }
.fgfe-squadhead .fgmenu-segrow { border: 0; padding: 0; grid-template-columns: auto auto; gap: var(--space-3); }

/* ── Responsive ──────────────────────────────────────────────────────────── */
@media (max-width: 1439px) {
  .fgfe-cols3 { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
  .fgfe-cols3 > *:nth-child(3) { grid-column: 1 / -1; }
}
@media (max-width: 1100px) {
  .fgfe-cols2, .fgfe-cols3 { grid-template-columns: minmax(0, 1fr); }
  .fgfe-cols3 > *:nth-child(3) { grid-column: auto; }
  .fgfe-hero { flex-direction: column; align-items: flex-start; gap: var(--space-4); }
  .fgmenu-segrow { grid-template-columns: minmax(0, 1fr); gap: var(--space-2); }
}

/* ===========================================================================
   COMPLETION — classes the four hub screens use that the block above missed.

   Found by extracting every class name the screens actually construct and
   diffing it against what the stylesheet defines, rather than by looking at
   screens one at a time. Sixteen were unstyled, which is why the World Cup
   screen rendered a 1000px logo over a centred column.
   =========================================================================== */

/* \`.fgfe-screen\` is applied ALONGSIDE \`.fgmenu-screen\`, whose centred flex
   column fights the safe-area layout. Neutralise it on these screens only. */
.fgmenu-screen.fgfe-screen {
  display: flex;
  align-items: stretch;
  gap: var(--space-5);
}
.fgmenu-screen.fgfe-screen > * { animation-name: fa-read; }

/* The top-bar wordmark. Unsized it renders at the PNG's natural width — that
   is the giant logo that swallowed the World Cup screen. */
.fgfe-topmark {
  width: 30px;
  height: 30px;
  min-width: 30px;
  object-fit: contain;
  display: block;
  margin-left: var(--space-3);
}
.fgfe-screen .fgmenu-logo { width: 30px; height: 30px; }

.fgfe-tabs { display: flex; align-items: center; gap: var(--space-1); margin-right: auto; margin-left: var(--space-5); }
/* The active tab is \`.fgfe-tabon\`, not \`.fgfe-tab-on\`. */
.fgfe-tabon, .fgfe-tab-on {
  background: var(--fa-accent-soft);
  color: var(--fa-accent);
  box-shadow: inset 0 0 0 1px var(--fa-accent-line);
}

/* Row internals are spans; without \`display:block\` the name and its subtitle
   run together — "ArgentinaCONMEBOL". */
.fgfe-rowtitle, .fgfe-rowsub, .fgfe-tiletitle, .fgfe-tiledesc { display: block; }

/* ── Tiles ───────────────────────────────────────────────────────────────── */
.fgfe-tiletitle {
  font-family: var(--fa-display);
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.02em;
}
.fgfe-tiledesc { margin-top: 3px; font-size: 12.5px; line-height: 1.5; color: var(--fa-steel-dim); }
.fgfe-tile-on { border-color: var(--fa-accent); background: var(--fa-accent-soft); }

/* ── "You" marker ────────────────────────────────────────────────────────
   The player's own club/nation in a 16-row table. Gold left edge so it is
   findable by peripheral vision while scrolling. */
.fgfe-me { position: relative; }
td.fgfe-me, .fgfe-me td { color: var(--fa-text); font-weight: 600; }
tr.fgfe-me td { background: var(--fa-accent-soft); }
tr.fgfe-me td:first-child { box-shadow: inset 3px 0 0 var(--fa-accent); }

/* Qualification cut-off in a group table — a rule, not a colour wash, so it
   reads as a line the teams above have crossed. */
.fgfe-qual td { border-bottom: 1px solid var(--fa-accent-line); }

/* ── Bracket ties ───────────────────────────────────────────────────────── */
.fgfe-tie {
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 0;
  border: 1px solid var(--fa-hairline);
  border-radius: var(--fa-r-md);
  background: rgba(255, 255, 255, 0.022);
  overflow: hidden;
  color: var(--fa-text);
  font-family: var(--fa-ui);
  text-align: left;
  cursor: pointer;
  transition: border-color var(--fa-t-fast) var(--fa-ease), transform var(--fa-t-fast) var(--fa-ease);
}
.fgfe-tie:hover, .fgfe-tie:focus-visible { border-color: var(--fa-accent-line); transform: translateY(-1px); outline: none; }
.fgfe-tie-me { border-color: var(--fa-accent-line); box-shadow: 0 0 0 1px var(--fa-accent-line); }
.fgfe-tieside {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  background: rgba(0, 0, 0, 0.2);
  font-size: 12.5px;
}
/* The winner is the one that reads bright; the loser recedes. In a bracket the
   eye follows the survivors upward, so that contrast is the information. */
.fgfe-tieside { color: var(--fa-steel-dim); }
.fgfe-tieside-won { color: var(--fa-text); font-weight: 700; }
.fgfe-tieside-won .fgfe-tiegoals { color: var(--fa-accent); }
.fgfe-tiename { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fgfe-tiegoals { font-family: var(--fa-display); font-size: 15px; font-weight: 700; font-variant-numeric: tabular-nums; }

/* ── Pick card internals ─────────────────────────────────────────────────── */
.fgfe-pick .fgfe-rowmain { flex: 1 1 auto; min-width: 0; }
.fgfe-pick .fgfe-rowtitle { font-size: 14px; }
.fgfe-pick .fgfe-rowsub { font-size: 10.5px; letter-spacing: 0.1em; }

/* ===========================================================================
   INTERACTION MOTION
   ---------------------------------------------------------------------------
   Entrances live in footy-arena.css. These are the responses — what the UI
   does when you touch it. Still transform/opacity only, still nothing looping.
   =========================================================================== */

/* A light sweep across the primary button on hover. Pure transform on a
   pseudo-element inside an overflow-hidden button: no repaint of the button. */
.fgmenu-primary, .fa-primary-cta { position: relative; overflow: hidden; }
.fgmenu-primary::after, .fa-primary-cta::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  width: 42%;
  left: -60%;
  background: linear-gradient(100deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.42) 50%, rgba(255,255,255,0) 100%);
  transform: skewX(-18deg);
  pointer-events: none;
}
.fgmenu-primary:hover::after, .fa-primary-cta:hover::after,
.fgmenu-primary:focus-visible::after, .fa-primary-cta:focus-visible::after {
  animation: fa-sweep 620ms var(--fa-ease);
}
@keyframes fa-sweep { to { left: 120%; } }

/* Tabs grow a gold underline from the centre rather than swapping background. */
.fgfe-tab { position: relative; }
.fgfe-tab::after {
  content: '';
  position: absolute;
  left: var(--space-4);
  right: var(--space-4);
  bottom: 4px;
  height: 2px;
  border-radius: 2px;
  background: var(--fa-accent);
  transform: scaleX(0);
  transition: transform var(--fa-t-fast) var(--fa-ease);
}
.fgfe-tab:hover::after, .fgfe-tabon::after { transform: scaleX(1); }

/* Pick cards lift and their rating counts up in weight. */
.fgfe-pick .fgfe-rating { transition: transform var(--fa-t-fast) var(--fa-ease), color var(--fa-t-fast) var(--fa-ease); }
.fgfe-pick:hover .fgfe-rating { transform: scale(1.12); }

/* Squad slots: the whole XI settles, then the selected one holds a gold ring. */
.fgfe-slot-on .fgfe-slotchip { box-shadow: 0 0 0 3px var(--fa-accent-soft), 0 6px 18px rgba(0, 0, 0, 0.55); }

/* Table rows reveal a gold left edge on hover — the same gesture as list rows,
   so a table and a list feel like the same object. */
.fgfe-table tbody tr td:first-child { box-shadow: inset 0 0 0 0 var(--fa-accent); transition: box-shadow var(--fa-t-fast) var(--fa-ease); }
.fgfe-table tbody tr:hover td:first-child { box-shadow: inset 3px 0 0 var(--fa-accent-line); }

/* ===========================================================================
   SQUAD SCREEN — the pitch has to be PORTRAIT
   ---------------------------------------------------------------------------
   Measured failure this fixes (live DOM, 1760x889):

     pitch box     505 x 332  -> aspect 1.52, LANDSCAPE
     slot spread   327px across, 152px deep

   The formation is drawn goal-to-goal: the GK sits at top:93%, the strikers
   near the top. So the axis carrying the formation's meaning — its depth — got
   152px while its width got 327px. A 4-4-2 was rendered at 46% of its true
   proportions, which is why every formation looked like the same flat cluster.

   The fix is two lines of intent:

     1. The pitch is explicitly PORTRAIT and sized from its HEIGHT, centred in
        its column. It previously had no aspect ratio at all and simply filled
        whatever box it was handed.

     2. Because the pitch's width is now capped by its height, a wide middle
        column would only add empty margin. The columns are rebalanced so the
        middle one is just wide enough for the diagram and the side panels take
        the rest. The conventional teams | pitch | squad order is unchanged.

   Why 5/7 (0.714) rather than a real pitch's 68/105 (0.648): at this size a
   true ratio leaves each wide player about 55px for a 40px chip plus a name.
   Widening slightly is the same convention broadcast formation graphics use —
   the diagram is wider than the pitch it describes so the flanks stay legible.
   =========================================================================== */

.fgfe-squadcols { grid-template-columns: minmax(0, 1fr) minmax(320px, 470px) minmax(0, 1fr); }

/* Height-driven and centred. min-height:0 on the flex child is what lets it
   shrink to fit rather than overflowing the panel. */
.fgfe-squadcols .fgfe-pitch {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  width: auto;
  aspect-ratio: 5 / 7;
  margin-inline: auto;
}

/* Reclaim height for the diagram from the identity band above it, which was
   spending 192px on a crest, a name and a formation picker. */
.fgfe-squadhead { padding: var(--space-4) var(--space-6); }
.fgfe-squadhead .fgfe-crest-lg { width: 40px; height: 40px; font-size: 13px; }

/* Names tighten now the pitch is narrower; the shadow keeps a 10px last name
   readable over the mown stripes. */
.fgfe-squadcols .fgfe-slotname {
  max-width: 72px;
  font-size: 10px;
  letter-spacing: 0;
  text-shadow: 0 1px 5px rgba(0, 0, 0, 0.95), 0 0 2px rgba(0, 0, 0, 0.85);
}
.fgfe-squadcols .fgfe-slotchip { width: 36px; height: 36px; font-size: 14px; }
.fgfe-squadcols .fgfe-slotpos { font-size: 8.5px; }
.fgfe-squadcols .fgfe-note { margin-top: var(--space-3); font-size: 11.5px; }

/* Narrow or short: stack, and let the pitch take its ratio from width instead
   so it never collapses to a sliver. */
@media (max-width: 1240px), (max-height: 700px) {
  .fgfe-squadcols { grid-template-columns: minmax(0, 1fr); }
  .fgfe-squadcols .fgfe-pitch { height: auto; width: min(100%, 340px); }
}
`;
